import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Building2, Plus, X, ArrowRight, AlertTriangle, Trash2, ChevronDown, ChevronRight, History } from "lucide-react";
import PanelState, { extractApiError } from "@/components/PanelState";

export default function AdminTenants() {
  const nav = useNavigate();
  const [tenants, setTenants] = useState([]);
  const [loadState, setLoadState] = useState({ status: "loading", error: null });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    slug: "",
    business_name: "",
    location: "",
    plan_tier: "pilot",
    servicetitan_tenant_id: "",
    rilla_account: "",
    watched_email: "",
    phone_config: "",
    sheets_workspace: "",
  });
  const [saving, setSaving] = useState(false);
  // Delete modal state — one target at a time
  const [deleteTarget, setDeleteTarget] = useState(null);  // tenant object or null
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [deleteForce, setDeleteForce] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState(null);  // {status, message}
  const [deleteResult, setDeleteResult] = useState(null); // success payload

  // Audit log (collapsible section)
  const [auditOpen, setAuditOpen] = useState(false);
  const [auditEntries, setAuditEntries] = useState([]);
  const [auditState, setAuditState] = useState({ status: "loading", error: null });
  const loadAudit = async () => {
    setAuditState({ status: "loading", error: null });
    try {
      const { data } = await api.get("/admin/audit-log", { params: { limit: 50 } });
      const items = Array.isArray(data) ? data : (Array.isArray(data?.items) ? data.items : []);
      setAuditEntries(items);
      setAuditState({ status: "ok", error: null });
    } catch (err) {
      setAuditEntries([]);
      setAuditState({ status: "error", error: extractApiError(err) });
    }
  };
  useEffect(() => { if (auditOpen) loadAudit(); }, [auditOpen]);

  const load = async () => {
    setLoadState({ status: "loading", error: null });
    try {
      const { data } = await api.get("/tenants");
      setTenants(Array.isArray(data) ? data : []);
      setLoadState({ status: "ok", error: null });
    } catch (err) {
      setTenants([]);
      setLoadState({ status: "error", error: extractApiError(err) });
    }
  };
  useEffect(() => {
    load();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/tenants", form);
      setOpen(false);
      setForm({
        slug: "",
        business_name: "",
        location: "",
        plan_tier: "pilot",
        servicetitan_tenant_id: "",
        rilla_account: "",
        watched_email: "",
        phone_config: "",
        sheets_workspace: "",
      });
      load();
    } finally {
      setSaving(false);
    }
  };

  // ---- delete flow ----
  const openDelete = (t) => {
    setDeleteTarget(t);
    setDeleteConfirm("");
    setDeleteForce(false);
    setDeleteError(null);
    setDeleteResult(null);
  };
  const closeDelete = () => {
    if (deleteBusy) return;
    setDeleteTarget(null);
    setDeleteConfirm("");
    setDeleteForce(false);
    setDeleteError(null);
    setDeleteResult(null);
  };
  // For slugless tenants, "DELETE" is the sentinel. For normal tenants, the slug itself.
  const expectedConfirm = deleteTarget?.slug || "DELETE";
  const confirmMatches = deleteConfirm === expectedConfirm;

  const performDelete = async () => {
    if (!deleteTarget || !confirmMatches) return;
    setDeleteBusy(true);
    setDeleteError(null);
    try {
      const params = { confirm: expectedConfirm };
      if (deleteForce) params.force = true;
      const { data } = await api.delete(`/tenants/${deleteTarget.id}`, { params });
      setDeleteResult(data);
      // refresh list; keep modal open so admin can review cascade counts
      await load();
      if (auditOpen) loadAudit();  // reflect the new event immediately
    } catch (err) {
      setDeleteError(extractApiError(err));
    } finally {
      setDeleteBusy(false);
    }
  };

  return (
    <div data-testid="admin-tenants-page" className="space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[.15em] text-[color:var(--cc-ink-muted)] font-semibold">
            Platform admin
          </div>
          <h1 className="font-display font-bold text-[26px] sm:text-[32px] tracking-tight text-[#0e1420] mt-1">Tenants</h1>
          <p className="text-[13.5px] text-[color:var(--cc-ink-muted)] mt-1">
            Every franchise currently running CurrentControl. White-glove provisioning.
          </p>
        </div>
        <button
          onClick={() => setOpen(true)}
          data-testid="add-tenant-btn"
          className="cc-btn-primary text-[13px]"
        >
          <Plus size={15} strokeWidth={1.8} /> Add tenant
        </button>
      </header>

      <PanelState
        status={loadState.status}
        error={loadState.error}
        isEmpty={tenants.length === 0}
        onRetry={load}
        emptyTitle="No tenants provisioned yet"
        emptyBody={
          <>
            The <code className="font-mono-data">tenants</code> collection returned zero records.
            Click <b>Add tenant</b> to onboard the first franchise.
          </>
        }
        testIdPrefix="admin-tenants"
      />

      <div className="grid md:grid-cols-2 gap-4">
        {tenants.map((t) => {
          const enterable = Boolean(t.slug);
          const enter = () => enterable && nav(`/${t.slug}/app/overview`);
          return (
            <div
              key={t.id}
              role="group"
              data-testid={`tenant-card-${t.id}`}
              className={`relative bg-white border border-[#e7e9ee] rounded-xl p-5 transition-all group ${
                enterable ? "hover:shadow-md hover:border-[#0072bc]/60 hover:-translate-y-[1px]" : "opacity-70"
              }`}
            >
              <button
                type="button"
                aria-label={`Delete ${t.business_name}`}
                data-testid={`tenant-card-delete-${t.id}`}
                onClick={(e) => { e.stopPropagation(); openDelete(t); }}
                className="absolute top-3 right-3 p-1.5 rounded-md text-[color:var(--cc-ink-muted)] hover:text-[#b3261e] hover:bg-[#fff0f0] transition-colors z-10"
              >
                <Trash2 size={15} strokeWidth={1.8} />
              </button>
              <div
                role={enterable ? "button" : undefined}
                tabIndex={enterable ? 0 : undefined}
                onClick={enter}
                onKeyDown={(e) => { if (enterable && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); enter(); } }}
                className={enterable ? "cursor-pointer" : ""}
                data-testid={`tenant-card-enter-${t.id}`}
              >
                <div className="flex items-start gap-3 pr-8">
                  <div className="cc-icon-badge">
                    <Building2 size={20} strokeWidth={1.6} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="font-display font-semibold text-[16px] text-[#0e1420]">{t.business_name}</div>
                      {enterable && (
                        <ArrowRight size={16} strokeWidth={1.8} className="text-[color:var(--cc-ink-muted)] group-hover:text-[#0072bc] shrink-0 mt-0.5 transition-colors" />
                      )}
                    </div>
                    <div className="text-[12px] text-[color:var(--cc-ink-muted)]">{t.location || "—"}</div>
                    {enterable ? (
                      <div className="text-[11px] font-mono-data text-[#0072bc] mt-0.5">/{t.slug}</div>
                    ) : (
                      <div className="text-[11px] font-mono-data text-[#b3261e] mt-0.5" data-testid={`tenant-card-slugless-${t.id}`}>
                        (no slug — legacy row)
                      </div>
                    )}
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      <span className="text-[10.5px] font-mono-data uppercase tracking-widest bg-[#e7f4fb] text-[#1c6a99] border border-[#c5e0ee] rounded px-2 py-0.5">
                        {t.plan_tier}
                      </span>
                      <span
                        className={`text-[10.5px] font-mono-data uppercase tracking-widest border rounded px-2 py-0.5 ${
                          t.status === "active"
                            ? "bg-[#e6f9f2] text-[#0e8a68] border-[#c1eddc]"
                            : "bg-[#fff6dc] text-[#8a6a1c] border-[#f0dfa5]"
                        }`}
                      >
                        {t.status}
                      </span>
                    </div>
                    <dl className="mt-4 space-y-1.5 text-[12px]">
                      <div className="flex justify-between gap-3">
                        <dt className="text-[color:var(--cc-ink-muted)]">ServiceTitan</dt>
                        <dd className="font-mono-data text-[#0e1420] truncate">{t.servicetitan_tenant_id || "—"}</dd>
                      </div>
                      <div className="flex justify-between gap-3">
                        <dt className="text-[color:var(--cc-ink-muted)]">Rilla</dt>
                        <dd className="font-mono-data text-[#0e1420] truncate">{t.rilla_account || "—"}</dd>
                      </div>
                      <div className="flex justify-between gap-3">
                        <dt className="text-[color:var(--cc-ink-muted)]">Watched email</dt>
                        <dd className="font-mono-data text-[#0e1420] truncate">{t.watched_email || "—"}</dd>
                      </div>
                      <div className="flex justify-between gap-3">
                        <dt className="text-[color:var(--cc-ink-muted)]">Sheets workspace</dt>
                        <dd className="font-mono-data text-[#0e1420] truncate">{t.sheets_workspace || "—"}</dd>
                      </div>
                    </dl>
                    {enterable && (
                      <div className="mt-3 text-[11px] text-[color:var(--cc-blue)] font-semibold group-hover:underline">
                        Enter tenant workspace →
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* ---- Audit log (collapsible) ---- */}
      <section
        data-testid="admin-audit-log-section"
        className="bg-white border border-[#e7e9ee] rounded-xl overflow-hidden"
      >
        <button
          type="button"
          onClick={() => setAuditOpen((v) => !v)}
          data-testid="admin-audit-log-toggle"
          className="w-full flex items-center justify-between px-5 py-3 hover:bg-[#fafbfc] text-left"
        >
          <span className="flex items-center gap-2">
            {auditOpen ? <ChevronDown size={16} strokeWidth={1.8} /> : <ChevronRight size={16} strokeWidth={1.8} />}
            <History size={15} strokeWidth={1.8} className="text-[color:var(--cc-ink-muted)]" />
            <span className="font-display font-semibold text-[14px] text-[#0e1420]">Audit log</span>
            <span className="text-[11.5px] text-[color:var(--cc-ink-muted)]">Recent admin actions</span>
          </span>
          {auditOpen && auditState.status === "ok" && (
            <span className="text-[11px] font-mono-data text-[color:var(--cc-ink-muted)]">
              {auditEntries.length} event{auditEntries.length === 1 ? "" : "s"}
            </span>
          )}
        </button>
        {auditOpen && (
          <div className="border-t border-[#eef1f6] p-4">
            <PanelState
              status={auditState.status}
              error={auditState.error}
              isEmpty={auditState.status === "ok" && auditEntries.length === 0}
              onRetry={loadAudit}
              emptyTitle="No admin events recorded yet"
              emptyBody={<>Tenant deletions and other privileged actions will appear here.</>}
              testIdPrefix="admin-audit-log"
              variant="inline"
            />
            {auditState.status === "ok" && auditEntries.length > 0 && (
              <ul className="divide-y divide-[#eef1f6]">
                {auditEntries.map((e) => (
                  <li key={e.id} className="py-2.5 flex items-start gap-3" data-testid={`audit-entry-${e.id}`}>
                    <span className="text-[10.5px] font-mono-data uppercase tracking-widest bg-[#fdecec] text-[#c73535] border border-[#f4c9c9] rounded px-2 py-0.5 mt-0.5 whitespace-nowrap">
                      {e.event_type}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] text-[#0e1420]">
                        <b>{e.business_name || "(no name)"}</b>
                        {e.tenant_slug ? (
                          <span className="ml-1.5 font-mono-data text-[11.5px] text-[color:var(--cc-ink-muted)]">/{e.tenant_slug}</span>
                        ) : (
                          <span className="ml-1.5 font-mono-data text-[11.5px] text-[#b3261e]">(slugless)</span>
                        )}
                      </div>
                      <div className="text-[11.5px] text-[color:var(--cc-ink-muted)] mt-0.5">
                        by <span className="font-mono-data">{e.actor_email || "unknown"}</span>
                        {" · "}
                        <span className="font-mono-data">{new Date(e.created_at).toLocaleString()}</span>
                        {e.force_used && (
                          <span className="ml-2 text-[10px] font-mono-data uppercase tracking-widest text-[#8a6a1c] bg-[#fff6dc] border border-[#f0dfa5] rounded px-1.5 py-0.5">
                            forced
                          </span>
                        )}
                      </div>
                      {e.cascade_counts && (
                        <div className="mt-1 text-[10.5px] font-mono-data text-[color:var(--cc-ink-muted)]">
                          {Object.entries(e.cascade_counts)
                            .filter(([, v]) => v > 0)
                            .map(([k, v]) => `${k}:${v}`)
                            .join(" · ") || "no rows removed"}
                        </div>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </section>

      {/* ---- Delete confirmation modal ---- */}
      {deleteTarget && (
        <div
          className="fixed inset-0 bg-black/45 backdrop-blur-sm z-50 flex items-center justify-center p-6"
          onClick={closeDelete}
          data-testid="delete-tenant-modal"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-2xl w-full max-w-[520px] shadow-2xl overflow-hidden"
          >
            <div className="p-5 border-b border-[#eef1f6] flex items-center justify-between">
              <div className="flex items-center gap-2">
                <AlertTriangle size={18} strokeWidth={1.8} className="text-[#b3261e]" />
                <div className="font-display font-bold text-[17px] text-[#0e1420]">Delete tenant</div>
              </div>
              <button
                type="button"
                onClick={closeDelete}
                disabled={deleteBusy}
                className="p-1.5 rounded-md hover:bg-[#f5f6f8] disabled:opacity-40"
                data-testid="delete-tenant-close"
              >
                <X size={16} strokeWidth={1.6} />
              </button>
            </div>

            {!deleteResult ? (
              <div className="p-5 space-y-4">
                <div className="bg-[#fff5f5] border border-[#f5c2c2] rounded-lg p-3 text-[12.5px] text-[#7a1c1c]">
                  <div>
                    You are about to permanently delete{" "}
                    <b>{deleteTarget.business_name || "(no name)"}</b>
                    {deleteTarget.slug ? (
                      <> (<code className="font-mono-data">/{deleteTarget.slug}</code>)</>
                    ) : (
                      <> <span className="font-mono-data">(no slug — legacy row)</span></>
                    )}{" "}
                    and cascade-wipe every associated row: users, kpi_jobs,
                    cancellations, followups, agent_runs, comp_records,
                    l10_meetings, meeting_attendance, and tech_kpis.
                  </div>
                  <div className="mt-1.5 opacity-90">This cannot be undone.</div>
                </div>

                <div>
                  <label className="block text-[11px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-semibold mb-1.5">
                    Type{" "}
                    <code className="font-mono-data text-[#0e1420]">
                      {expectedConfirm}
                    </code>{" "}
                    to confirm
                  </label>
                  <input
                    autoFocus
                    value={deleteConfirm}
                    data-testid="delete-tenant-confirm-input"
                    onChange={(e) => setDeleteConfirm(e.target.value)}
                    placeholder={expectedConfirm}
                    className="w-full border border-[#e7e9ee] rounded-lg px-3 py-2 text-[13px] font-mono-data outline-none focus:border-[#b3261e]"
                  />
                  {!deleteTarget.slug && (
                    <div className="text-[11px] text-[color:var(--cc-ink-muted)] mt-1">
                      This tenant has no slug, so the sentinel{" "}
                      <code className="font-mono-data">DELETE</code> is used
                      instead.
                    </div>
                  )}
                </div>

                <label className="flex items-start gap-2 text-[12.5px] text-[#0e1420] cursor-pointer">
                  <input
                    type="checkbox"
                    checked={deleteForce}
                    data-testid="delete-tenant-force"
                    onChange={(e) => setDeleteForce(e.target.checked)}
                    className="mt-0.5"
                  />
                  <span>
                    <b>Force</b> — cascade even if this tenant has users or
                    kpi_jobs. Required for populated tenants; safe to leave
                    unchecked when cleaning up empty legacy rows.
                  </span>
                </label>

                {deleteError && (
                  <div
                    data-testid="delete-tenant-error"
                    className="bg-[#fff5f5] border border-[#f5c2c2] rounded-lg p-3 text-[12.5px] text-[#7a1c1c]"
                  >
                    <div className="font-semibold">Delete failed</div>
                    <div className="mt-1 font-mono-data text-[11.5px] break-all">
                      <span className="uppercase tracking-widest">Status </span>
                      <span data-testid="delete-tenant-error-status">
                        {deleteError.status ?? "network"}
                      </span>
                      <span className="mx-2 opacity-60">·</span>
                      <span data-testid="delete-tenant-error-detail">
                        {deleteError.message}
                      </span>
                    </div>
                  </div>
                )}

                <div className="flex justify-end gap-2 pt-2">
                  <button
                    type="button"
                    onClick={closeDelete}
                    disabled={deleteBusy}
                    className="text-[12.5px] font-semibold px-4 py-2 rounded-md border border-[#e7e9ee] hover:bg-[#f5f6f8] disabled:opacity-40"
                    data-testid="delete-tenant-cancel"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={performDelete}
                    disabled={!confirmMatches || deleteBusy}
                    data-testid="delete-tenant-submit"
                    className="text-[12.5px] font-semibold px-4 py-2 rounded-md bg-[#b3261e] text-white hover:bg-[#8f1e18] disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {deleteBusy ? "Deleting…" : "Delete tenant"}
                  </button>
                </div>
              </div>
            ) : (
              <div className="p-5 space-y-3" data-testid="delete-tenant-success">
                <div className="bg-[#e6f9f2] border border-[#c1eddc] rounded-lg p-3 text-[12.5px] text-[#0e5a3f]">
                  Deleted{" "}
                  <b>{deleteResult.business_name || deleteResult.tenant_id}</b>
                  {deleteResult.slug && (
                    <> (<code className="font-mono-data">/{deleteResult.slug}</code>)</>
                  )}
                  {" "}and all associated rows.
                </div>
                <div className="text-[11.5px] font-mono-data text-[#0e1420]">
                  <div className="uppercase tracking-widest text-[color:var(--cc-ink-muted)] mb-1">
                    Cascade counts
                  </div>
                  <ul className="space-y-0.5">
                    {Object.entries(deleteResult.deleted || {}).map(([k, v]) => (
                      <li key={k} className="flex justify-between border-b border-[#eef1f6] py-1">
                        <span>{k}</span>
                        <span data-testid={`delete-tenant-cascade-${k}`}>{v}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={closeDelete}
                    data-testid="delete-tenant-done"
                    className="text-[12.5px] font-semibold px-4 py-2 rounded-md bg-[#0e1420] text-white hover:bg-black"
                  >
                    Done
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {open && (
        <div
          className="fixed inset-0 bg-black/45 backdrop-blur-sm z-50 flex items-center justify-center p-6"
          onClick={() => setOpen(false)}
          data-testid="add-tenant-modal"
        >
          <form
            onSubmit={submit}
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-2xl w-full max-w-[560px] shadow-2xl overflow-hidden"
          >
            <div className="p-5 border-b border-[#eef1f6] flex items-center justify-between">
              <div className="font-display font-bold text-[17px] text-[#0e1420]">Provision new tenant</div>
              <button type="button" onClick={() => setOpen(false)} className="p-1.5 rounded-md hover:bg-[#f5f6f8]">
                <X size={16} strokeWidth={1.6} />
              </button>
            </div>
            <div className="p-5 grid gap-3 max-h-[70vh] overflow-y-auto">
              {[
                ["slug", "URL slug (e.g. mre-swmo)", true],
                ["business_name", "Business name", true],
                ["location", "Location", true],
                ["servicetitan_tenant_id", "ServiceTitan tenant ID", false],
                ["rilla_account", "Rilla account", false],
                ["watched_email", "Watched email", false],
                ["phone_config", "Phone / Dialpad config", false],
                ["sheets_workspace", "Google Sheets workspace", false],
              ].map(([key, label, req]) => (
                <div key={key}>
                  <label className="block text-[11px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-semibold mb-1.5">
                    {label}
                  </label>
                  <input
                    required={req}
                    value={form[key]}
                    data-testid={`tenant-form-${key}`}
                    onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                    className="w-full border border-[#e7e9ee] rounded-lg px-3 py-2 text-[13px] outline-none focus:border-[#0072bc]"
                  />
                </div>
              ))}
              <div>
                <label className="block text-[11px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-semibold mb-1.5">
                  Plan tier
                </label>
                <select
                  value={form.plan_tier}
                  data-testid="tenant-form-plan_tier"
                  onChange={(e) => setForm({ ...form, plan_tier: e.target.value })}
                  className="w-full border border-[#e7e9ee] rounded-lg px-3 py-2 text-[13px] outline-none focus:border-[#0072bc] bg-white"
                >
                  <option value="pilot">pilot</option>
                  <option value="starter">starter</option>
                  <option value="growth">growth</option>
                  <option value="enterprise">enterprise</option>
                </select>
              </div>
            </div>
            <div className="p-5 border-t border-[#eef1f6] flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="text-[12.5px] font-semibold px-4 py-2 rounded-md border border-[#e7e9ee] hover:bg-[#f5f6f8]"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                data-testid="tenant-form-submit"
                className="cc-btn-primary text-[12.5px] py-2 px-4 disabled:opacity-60"
              >
                {saving ? "Provisioning…" : "Create tenant"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
